import { FC, ReactNode } from "react";
import Typography from "../Typography";

export type FeatureGridItem = {
  icon: ReactNode;
  h3: ReactNode;
  body: ReactNode;
};

type FeatureGridProps = {
  sectionTitle: ReactNode;
  description?: ReactNode;
  features: FeatureGridItem[];
  columns?: 3 | 4;
};

const FeatureGrid: FC<FeatureGridProps> = ({
  sectionTitle,
  description,
  features,
  columns,
}) => {
  // Default: 4 cols for 8 features, 3 cols for everything else
  // (master spec §"FeatureGrid"). Mobile collapses to 1 column.
  const resolvedColumns = columns ?? (features.length >= 8 ? 4 : 3);
  const gridCols =
    resolvedColumns === 4
      ? "md:grid-cols-2 lg:grid-cols-4"
      : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <section className="max-w-[1500px] px-3 md:px-12 mx-auto my-32 md:my-48 text-center">
      <Typography
        variant="h2"
        component="h2"
        className="max-w-3xl mx-auto text-center"
      >
        {sectionTitle}
      </Typography>
      {description && (
        <Typography
          variant="body1"
          className="mt-6 text-slate-500 max-w-2xl mx-auto"
        >
          {description}
        </Typography>
      )}
      <div className={`mt-16 grid grid-cols-1 ${gridCols} gap-8 text-left`}>
        {features.map((feature, index) => (
          <div
            key={index}
            className="rounded-2xl bg-slate-100 p-6 flex flex-col gap-3"
          >
            <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg bg-violet-100 text-violet-600 [&>svg]:w-5 [&>svg]:h-5">
              {feature.icon}
            </span>
            <Typography variant="subtitle1" className="block">
              {feature.h3}
            </Typography>
            <Typography variant="body2" className="text-slate-500">
              {feature.body}
            </Typography>
          </div>
        ))}
      </div>
    </section>
  );
};

export default FeatureGrid;
